import { FormEvent } from 'react';
import styled from 'styled-components';
import BigNumber from 'bignumber.js';

import InProgressIcon from '#/assets/InProgressIcon';

import { DEPOSITS_WITHDRAWALS_DOCS_URL } from '#/routes-external';

import * as Alert from '#/components/common/alert';
import ShowMoreText from '#/components/shared/ShowMoreText';
import ExternalLink from '#/components/shared/ExternalLink';

import { useDepositFormView } from '#/features/deposit/form-context';
import { useDepositView } from '#/features/deposit/deposit-context';
import { logException } from '#/features/logging/logging';

import BridgeEnableToken from './BridgeEnableToken';
import InputAmount from './InputAmount';
import InputAsset from './InputAsset';
import Button from './Button';
import AccountBalance from './AccountBalance';
import { useDepositToEthereumBridge } from './deposit';

export default function DepositForm() {
  const formView = useDepositFormView();
  const depositView = useDepositView();
  const depositToEthereumBridge = useDepositToEthereumBridge();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    depositToEthereumBridge(
      BigNumber(formView.amount.value),
      formView.asset.symbol,
    ).catch((err) => {
      const message = 'Error submitting deposit form';
      logException(new Error(message, { cause: err }));
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <InputAsset />
      <InputAmount />
      {formView.error.length > 0 && <Alert.Error>{formView.error}</Alert.Error>}
      {formView.needsAllowance && <BridgeEnableToken />}
      {!formView.needsAllowance && <Info />}
      {depositView.error.length > 0 && (
        <Alert.Error>
          <ShowMoreText maxRows={3}>{depositView.error}</ShowMoreText>
        </Alert.Error>
      )}
      <AccountBalance />
      <Button type="submit" disabled={!formView.isValid || formView.isBusy}>
        {formView.isBusy ? (
          <InProgressIcon size={22} />
        ) : (
          'Initiate Deposit to Bridge'
        )}
      </Button>
    </Form>
  );
}

const Info = () => (
  <InfoContainer>
    <ShowMoreText maxRows={2}>
      Deposits are a two-step process. First initiate a deposit from your
      Ethereum wallet to the StarkNet bridge. Second, initiate deposit from the
      StarkNet bridge to Paraclear via the Deposit / Withdraw table. These
      transactions may take several minutes depending on network conditions. For
      more information check out our{' '}
      <ExternalLink href={DEPOSITS_WITHDRAWALS_DOCS_URL}>docs</ExternalLink>.
    </ShowMoreText>
  </InfoContainer>
);

const InfoContainer = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  line-height: 18px;
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;
